import React, { useState, useRef } from "react";
import { IoIosArrowDroprightCircle } from "react-icons/io";
import currentIdentityAvatar2 from "../../../../../../../assets/currentIdentityAvatar2.png";
import { FaPlay, FaPause } from "react-icons/fa";

const FutureCardPrompt = ({ formData, onContinue, hideContinue }) => { 
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Audio Player Control
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Main Button Handler
  const handleStartBuilding = () => {
    if (onContinue && typeof onContinue === "function") {
      onContinue(formData);
    }
  };

  return (
    <>
      <div className="h-auto flex items-center justify-center p-8">
        <div className="max-w-2xl w-full pb-20">
          {/* Intro Text */}
          <p className="font-gotham font-normal text-base md:text-lg text-[#888888] text-center mb-1">
            Now that we've looked inward, let's look ahead.
          </p>
          <h1 className="text-3xl md:text-2xl lg:text-[28px] 2xl:text-[32px] font-medium text-center font-gotham text-[#323232] mb-6 animate-fade-in">
            Let's build your Future Identity Card
          </h1>

          <div className="relative flex justify-center">

          {/* 🔹 Horizontal line on the LEFT side — centered */}
          <div className="absolute bottom-[50%] left-0 -translate-x-[80%] w-[75%] h-[5px] bg-[#E0E0E0]"></div>

          {/* Preview Card */}
          <div className="bg-white border border-[#707070] rounded-3xl shadow-2xl overflow-hidden max-w-[443px] mx-auto animate-scale-in">
            
            {/* Card Header - Teal background */}
            <div className="bg-[#54B0AF] h-24 relative">
              {/* Profile Avatar */}
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
                <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center overflow-hidden border-4 border-white shadow-xl">
                  <img 
                    src={formData?.profileImage || currentIdentityAvatar2} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="pt-20 px-8 pb-8">
              
              {/* User Name */}
              <h2 className="text-center text-2xl font-semibold font-gotham text-[#1C1C1C] mb-2">
                {formData?.userName || "User Name"}
              </h2>

              {/* Current Designation/Title */}
              <p className="text-center text-sm text-[#B3B3B3] font-gotham font-medium uppercase tracking-wider mb-6">
                {formData?.designation || "Current Role"}
              </p>

              {/* Future Data Preview (Placeholder) */}
              <div className="space-y-2 text-sm pb-4 border-b border-t pt-4 border-[#E3E3E3]">
                {/* Future Location */}
                <div className="flex items-center">
                  <p className="text-[#B3B3B3] font-gotham font-medium md:text-[12px] text-xs uppercase tracking-wide w-40">
                    Location
                  </p>
                  <p className="mx-2 text-[#B3B3B3]">:</p>
                  <div className="h-4 bg-[#F0F0F0] rounded-[10px] w-24 md:w-40 animate-pulse ml-4"></div>
                </div>

                {/* Future Salary */}
                <div className="flex items-center">
                  <p className="text-[#B3B3B3] font-gotham font-medium md:text-[12px] text-xs uppercase tracking-wide w-40">
                    Salary
                  </p>
                  <p className="mx-2 text-[#B3B3B3]">:</p>
                  <div className="h-4 bg-[#F0F0F0] rounded-[10px] w-24 md:w-40 animate-pulse ml-4"></div>
                </div>

                {/* Future Timeline */}
                <div className="flex items-center">
                  <p className="text-[#B3B3B3] font-gotham font-medium md:text-[12px] text-xs uppercase tracking-wide w-40">
                    Timeline
                  </p>
                  <p className="mx-2 text-[#B3B3B3]">:</p>
                  <div className="h-4 bg-[#F0F0F0] rounded-[10px] w-24 md:w-40 animate-pulse ml-4"></div>
                </div>
              </div>

              {/* Known For Section */}
              <div className="mb-6">
                <p className="text-center text-sm md:text-[12px] font-gotham font-bold text-[#54B0AF] uppercase tracking-wide mt-4 mb-4">
                  Known For
                </p>
                <div className="space-y-0.5">
                  {[1, 2, 3].map((i) => (
                    <li key={i} className="flex justify-center items-start gap-2">
                      <span className="text-lg md:text-xl lg:text-4xl leading-none text-[#F0F0F0]">
                        •
                      </span>
                      <span className="text-xs md:text-[16px] mt-3 text-[#323232] font-gotham font-medium leading-relaxed flex-1">
                        <div className="h-3 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                      </span>
                    </li>
                  ))}
                </div>
              </div>

              {/* Voice Note Section */}
              <div className="bg-[#D6EFEF] rounded-xl p-4">
                <p className="text-center text-sm md:text-[12px] font-gotham font-bold text-[#54B0AF] uppercase tracking-wide mb-3">
                  Message for Future Self
                </p>
                
                {/* Audio Player */}
                <div className="flex items-center gap-3">
                  {/* Play/Pause Button */}
                  <button
                    onClick={togglePlayPause}
                    className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!formData?.futureVoiceNote}
                    title={formData?.futureVoiceNote ? "Play/Pause" : "No recording"}
                  >
                    {isPlaying ? (
                      <FaPause className="w-4 h-4 text-white" fill="white" />
                    ) : ( 
                      <FaPlay className="w-4 h-4 text-white ml-0.5" fill="white" />
                    )}
                  </button>
                  
                  {/* Audio Visualizer */}
                  <div className="flex-1 flex gap-1 items-center">
                    {[...Array(38)].map((_, i) => (
                      <div 
                        key={i} 
                        className="w-1 bg-[#707070] rounded-full transition-all"
                        style={{ 
                          height: `${Math.random() * 16 + 8}px`,
                          opacity: isPlaying ? 0.8 : 0.4
                        }}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Hidden Audio Element */}
                {formData?.futureVoiceNote && (
                  <audio
                    ref={audioRef}
                    src={formData.futureVoiceNote}
                    onEnded={() => setIsPlaying(false)}
                    className="hidden"
                  />
                )}

                {/* No Recording Message */}
                {!formData?.futureVoiceNote && (
                  <p className="text-center text-xs text-gray-500 mt-2">
                    No voice note recorded
                  </p>
                )}
              </div>
            </div>
          </div>

          </div>

          {/* Start Button */}
          {!hideContinue && (
            <div className="text-center mt-8">
              <button
                onClick={handleStartBuilding}
                className="w-full max-w-[443px] px-4 py-4 bg-[#54B0AF] flex justify-between text-white rounded-[20px] font-gotham font-medium text-lg hover:bg-teal-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 items-center gap-3 mx-auto"
              >
                Start Building Now
                <IoIosArrowDroprightCircle className="w-5 h-5 md:w-7 md:h-7 text-lg" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// CSS Animations
const style = document.createElement('style');
style.textContent = `
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes scale-in {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .animate-fade-in {
    animation: fade-in 0.6s ease-out;
  }
  
  .animate-scale-in {
    animation: scale-in 0.5s ease-out;
  }
`;
document.head.appendChild(style);

export default FutureCardPrompt;